import PropTypes from "prop-types";
import { DataGrid } from "@mui/x-data-grid";

import MDBox from "components/MDBox";

import StatusBadge from "components/EntityTable/StatusBadge";
import RowActionMenu from "components/EntityTable/RowActionMenu";
import { ROWS_PER_PAGE_OPTIONS, DEFAULT_PAGE_SIZE } from "config/pagination";

/**
 * Shared table component — a thin <DataGrid> wrapper that standardizes
 * pagination wiring and styling across the Users / Websites / Forms /
 * Email-templates tables.
 *
 * `paginationMode="server"` (default) expects `rowCount` + `page` +
 * `onPageChange` from a paginated hook. `paginationMode="client"` lets the
 * DataGrid paginate the rows it is given.
 *
 * Exports `StatusBadge` and `RowActionMenu` for use inside column definitions.
 */
function EntityTable({
  rows,
  columns,
  getRowId,
  loading,
  paginationMode,
  rowCount,
  page,
  pageSize,
  rowsPerPageOptions,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  autoHeight,
}) {
  const paginationProps =
    paginationMode === "server" ? { paginationMode: "server", rowCount, page, onPageChange } : {};

  return (
    <MDBox
      sx={{
        "& .MuiDataGrid-root": { border: "none" },
        "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
        "& .MuiDataGrid-row": onRowClick ? { cursor: "pointer" } : undefined,
        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
        "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
          outline: "none",
        },
      }}
    >
      <DataGrid
        autoHeight={autoHeight}
        rows={rows}
        columns={columns}
        getRowId={getRowId}
        loading={loading}
        pagination
        pageSize={pageSize}
        rowsPerPageOptions={rowsPerPageOptions}
        onPageSizeChange={onPageSizeChange}
        onRowClick={onRowClick}
        disableSelectionOnClick
        disableColumnMenu
        {...paginationProps}
      />
    </MDBox>
  );
}

EntityTable.defaultProps = {
  getRowId: (row) => row.id,
  loading: false,
  paginationMode: "server",
  rowCount: 0,
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
  onPageChange: undefined,
  onPageSizeChange: undefined,
  onRowClick: undefined,
  autoHeight: true,
};

EntityTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  getRowId: PropTypes.func,
  loading: PropTypes.bool,
  paginationMode: PropTypes.oneOf(["server", "client"]),
  rowCount: PropTypes.number,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onRowClick: PropTypes.func,
  autoHeight: PropTypes.bool,
};

export default EntityTable;
export { StatusBadge, RowActionMenu };
